import { ToggleButtonGroup } from "@mui/material";
import { FC, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { ErrorsDisplayContainer } from "@ryde/controls";
import { useCrudEditContext } from "@ryde/redux";
import {
  archiveRelationErrorAtomFamily,
  DATA_E2E_KEY,
  errorsCrudContentAtomFamily,
  IRideDto,
  isValidPositiveNumber,
  RideEntity,
  RideFormFields,
  rideIncidentsTabCountsAF,
  RideTabsEnum,
  useHasPermissions,
  useIsMobile,
  useSystemParams,
} from "@ryde/shared";

import { RelatedEntitiesErrorButton } from "../../related-entities";
import { useRideActiveTab } from "./hooks";
import { RideTabsTabIncidents } from "./incident-tab";
import { RideEditTopInfoContainer } from "./ride-edit-top-info-container";
import { RideHeaderInfosMobile } from "./ride-header-infos-mobile";
import { RideMap } from "./ride-map";
import { RideTabsBottomBlocks } from "./ride-tabs-bottom-blocks";
import { RideTabsTabAdvanced } from "./ride-tabs-tab-advanced";
import { RideTabsTabBasic } from "./ride-tabs-tab-basic";
import { RideTabsToggleButton } from "./ride-tabs.styled";
import {
  RideIncidentsTabNewCountStyled,
  RideTabsEarsContainer,
  RideTabsErrorContainer,
  RideTabsMobileHeaderWrap,
  RideTabsScrollableContent,
  RideTabsWrapperStyled,
} from "./ride.styled";
import { RideHeaderSavedRouteIndicator } from "./station-route-indicator";

export const RideTabsWrapper: FC = () => {
  const { entity, id: rideRecordId } = useCrudEditContext<IRideDto>();
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const serverErrors = useRecoilValue(errorsCrudContentAtomFamily(entity));
  const hasArchiveRelationError = useRecoilValue(
    archiveRelationErrorAtomFamily(entity),
  );
  const incidentsTabCounts = useRecoilValue(
    rideIncidentsTabCountsAF(rideRecordId ?? 0),
  );

  const { hasPermissions: hasIncidentRead } = useHasPermissions({
    entity: "incident",
    action: "read",
  });
  const { enableIncidents } = useSystemParams();
  const hasPersistedRideId = isValidPositiveNumber(rideRecordId);
  const showIncidentsRideTab =
    Boolean(enableIncidents) && hasIncidentRead && hasPersistedRideId;

  const { activeTab, handleTabSwitch } = useRideActiveTab({
    entity,
    rideRecordId,
    isMobile,
    showIncidentsRideTab,
  });

  const {
    formState: { errors, isValid },
  } = useFormContext<Partial<RideEntity>>();

  const watchedDirectionValue = useWatch({ name: RideFormFields.direction });
  const watchedStartDate = useWatch({ name: RideFormFields.rideStart });
  const watchedEndDate = useWatch({ name: RideFormFields.rideEnd });

  const isFormDataReady = useMemo(() => {
    /* protection - datepickers inject NOW to form values before data is loaded from api in some cases
     blocking form validation */
    return Boolean(watchedStartDate && watchedEndDate);
  }, [watchedEndDate, watchedStartDate]);

  const showRideTabsBottomBlocks =
    !isMobile ||
    activeTab === RideTabsEnum.Basic ||
    activeTab === RideTabsEnum.Advanced ||
    activeTab === RideTabsEnum.Incidents;

  return (
    isFormDataReady && (
      <RideTabsWrapperStyled className="RideDrawer-Form">
        <RideEditTopInfoContainer />

        {isMobile && (
          <RideTabsMobileHeaderWrap>
            <RideHeaderInfosMobile />
          </RideTabsMobileHeaderWrap>
        )}

        <RideTabsEarsContainer>
          <div
            className="ride-tabs-toggle-button-group-wrap"
            {...{ [DATA_E2E_KEY]: "ride-tabs-toggle-button-group-wrap" }}
          >
            <ToggleButtonGroup
              color="primary"
              size="small"
              value={activeTab}
              exclusive
              onChange={handleTabSwitch}
              aria-label="Platform"
            >
              <RideTabsToggleButton
                value={RideTabsEnum.Basic}
                disabled={activeTab === RideTabsEnum.Basic}
              >
                {t("selectListLabel.basic")}
              </RideTabsToggleButton>
              <RideTabsToggleButton
                value={RideTabsEnum.Advanced}
                disabled={
                  activeTab === RideTabsEnum.Advanced || !watchedDirectionValue
                }
              >
                {t("selectListLabel.advanced")}
              </RideTabsToggleButton>
              {showIncidentsRideTab && (
                <RideTabsToggleButton
                  value={RideTabsEnum.Incidents}
                  disabled={activeTab === RideTabsEnum.Incidents}
                >
                  {t("ride.incidents.tabLabel")}
                  {incidentsTabCounts && incidentsTabCounts.total > 0 ? (
                    <>
                      {" ("}
                      <RideIncidentsTabNewCountStyled>
                        {incidentsTabCounts.newCount}
                      </RideIncidentsTabNewCountStyled>
                      {`/${incidentsTabCounts.total})`}
                    </>
                  ) : null}
                </RideTabsToggleButton>
              )}
              {isMobile && (
                <RideTabsToggleButton
                  value={RideTabsEnum.Map}
                  disabled={
                    activeTab === RideTabsEnum.Map || !watchedDirectionValue
                  }
                >
                  {t("selectListLabel.map")}
                </RideTabsToggleButton>
              )}
            </ToggleButtonGroup>
          </div>
          <RideHeaderSavedRouteIndicator />
        </RideTabsEarsContainer>
        <RideTabsErrorContainer>
          <ErrorsDisplayContainer
            formErrors={isValid ? undefined : errors}
            serverErrors={serverErrors}
            marginLeft="22px"
          />
          {hasArchiveRelationError && hasPersistedRideId && (
            <RelatedEntitiesErrorButton entity="ride" entityId={rideRecordId} />
          )}
        </RideTabsErrorContainer>

        <RideTabsScrollableContent>
          {activeTab === RideTabsEnum.Basic && <RideTabsTabBasic />}
          <RideTabsTabAdvanced tab={activeTab} />
          {showIncidentsRideTab && (
            <div hidden={activeTab !== RideTabsEnum.Incidents}>
              <RideTabsTabIncidents />
            </div>
          )}
          {isMobile && activeTab === RideTabsEnum.Map && <RideMap />}
          {showRideTabsBottomBlocks && <RideTabsBottomBlocks />}
        </RideTabsScrollableContent>
      </RideTabsWrapperStyled>
    )
  );
};
